import { filter, takeUntil, tap } from 'rxjs/operators';
import { SessionsService } from '@hub-services/sessions.service';
import { EditingDataService } from '@hub-services/editing-data.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { Store } from '@ngrx/store';
import { setSessionData } from '@hub-store/session/session.actions';
import { environment } from '@hub-env/environment';
import { VisualStyle, VisualColor, SnackbarComponent, MenuListItem, GroupedListItem } from '@atpco/hub-ui';
import { Observable, Subject } from 'rxjs';
import { getAppSnackbar } from '@hub-store/messages/messages.selectors';
import { DataService } from '@hub-services/data.service';

import { FullstoryService } from '@hub-services/fullstory.service';
import { UserflowService } from '@hub-services/userflow.service';
import { AuthService } from './auth/auth.service';
import { FaroObservabilityService, extractViewName, normalizeUrlPath } from '@hub-services/faro-observability.service';

export { extractViewName, normalizeUrlPath };

@Component({
  selector: 'app-root',
  template: `
    <rh-ui-icons></rh-ui-icons>
    <rh-lib-icons></rh-lib-icons>
    <router-outlet *ngIf="!isIframe"></router-outlet>
    <ng-container *ngIf="sub$ | async"></ng-container>
    <rh-ui-snackbar #snackbarRef [colorInput]="snackbarConfig?.color" [messageInput]="snackbarConfig?.message" [styleInput]="snackbarConfig?.style"
    [actionInput]="snackbarConfig?.action" [durationInput]="5000"></rh-ui-snackbar>
    <rh-help-button *ngIf="!isIframe"></rh-help-button>

  `,
  host: {
    class: `environment--${environment.name}`
  },
})
export class AppComponent implements OnInit {
  @ViewChild('snackbarRef', { read: SnackbarComponent }) snackbarRef!: SnackbarComponent;
  // invariant: ngOnInit's getAppSnackbar tap populates snackbarConfig before displaySnackbar
  snackbarConfig!: { style: VisualStyle | null, message: string | null, action: string | null, color: VisualColor | null; };
  // invariant: ngOnInit populates sub$ before the async pipe subscribes
  sub$!: Observable<any>;
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  userInput: any = {
    name: 'Joe Smith',
    org: 'ATPCO',
    email: 'jsmith@atpco.net',
    username: 'ATPCOJS',
    avatar: {
      src: 'https://rh-file-upload.s3.us-west-2.amazonaws.com/placeholders/airplane-avatar.png',
      alt: 'Plane Avatar',
    },
  };

  organizationShortcode = 'AA';
  organizationLabel = 'American Airlines';
  user: any;
  userMenuList: MenuListItem[] = [
    {
      name: 'Sign out',
      id: 'sign-out',
    },
  ];

  breadcrumbsData: MenuListItem[] = [
    {
      name: 'Home',
      id: 'home',
    },
    {
      name: 'Aircrafts',
    },
  ];
  constructor(
    private router: Router,
    private sessionsService: SessionsService,
    private editingDataService: EditingDataService,
    private store: Store,
    private dataService: DataService,
    private fullstoryService: FullstoryService,
    private userflowService: UserflowService,
    private authService: AuthService,
    private faroObservabilityService: FaroObservabilityService,
  ) {
    this.sessionsService.getSession(false).subscribe((session: any) => {
      this.store.dispatch(setSessionData({ data: session }))

      // Set Faro session attributes for trace correlation (no PII)
      if (session?.data) {
        const partnerCode = session.data.partner_carrier_code || session.data.partner_uid;
        const partnerType = session.data.partner_type;
        this.faroObservabilityService.setSessionAttributes({
          ...(partnerCode && { 'partner.code': partnerCode }),
          ...(partnerType && { 'partner.type': partnerType }),
        });
      }
    });
  }

  ngOnInit() {
    this.fullstoryService.init();
    this.userflowService.init();
    this.router.events
      .pipe(filter((e: any) => e instanceof RoutesRecognized))
      .subscribe((e: any) => {
        const regEx = /(\/view)/;
        const regExUaEditing = /(edit|basics|targeting|media)/;
        const path = e.urlAfterRedirects;
        localStorage.setItem('rhLastPath', localStorage.getItem('rhCurrentPath') || '');
        localStorage.setItem('rhCurrentPath', path);
        if (regEx.test(path)) {
          localStorage.setItem('rhLastSummaryPath', path);
        }
        if (!regExUaEditing.test(path)) {
          this.editingDataService.clearData();
        }

        // Track route changes in Faro for trace correlation
        this.faroObservabilityService.setView(extractViewName(path));
      });
    this.sub$ = this.store.select(getAppSnackbar)
      .pipe(
        filter(config => !!config.message),
        tap(config => {
          this.snackbarConfig = config;
          this.snackbarRef.displaySnackbar();
        })
      );


      this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

      // All MSAL event subscriptions now live in AuthService.initialize().
      // AppComponent reacts to the derived auth state via authService.accounts$:
      //   - LOGIN_FAILURE / ACQUIRE_TOKEN_FAILURE handling (classifier-routed)
      //   - LOGIN_SUCCESS / ACQUIRE_TOKEN_SUCCESS / SSO_SILENT_SUCCESS
      //     (setActiveAccount on signUpSignIn policy completion)
      //   - ACCOUNT_ADDED / ACCOUNT_REMOVED (cross-tab account state)
      //   - LOGOUT_SUCCESS (cross-tab logout response → navigate to /)
      this.authService.accounts$
        .pipe(takeUntil(this._destroying$))
        .subscribe(accounts => {
          this.loginDisplay = accounts.length > 0;
          if (accounts.length > 0) {
            const account = accounts[0];
            const user = {
              name: account.name || 'test',
              email: account.idTokenClaims?.email,
              familyName: account.idTokenClaims?.family_name || 'surname',
            };
            this.dataService.changeUser(user);
          }
        });

      // subscribe to user change
      this.dataService.getUserAsObservable().pipe(takeUntil(this._destroying$)).subscribe({
        next: (user) => {
          this.user = user;
        },
      });
  }
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
